import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

/**
 * Tela de definição de nova senha (DEM-2044).
 *
 * Recebe o usuário após clicar no link de recuperação enviado por email. O
 * Supabase Auth SDK detecta o fragmento da URL (#access_token / type=recovery)
 * e emite o evento `PASSWORD_RECOVERY`, estabelecendo uma sessão temporária.
 * Com essa sessão, gravamos a nova senha via `updateUser`.
 *
 * Tratamento de token expirado/inválido (AC#3/AC#4): se o link estiver
 * expirado, inválido ou já usado, o Supabase coloca `error=...` /
 * `error_code=otp_expired` no fragmento da URL e NÃO emite a sessão de
 * recovery. Detectamos esse caso e mostramos mensagem clara + opção de
 * re-solicitar o reset (/forgot-password).
 *
 * NÃO reintroduz signup: é só troca de senha de conta existente.
 */
type TokenState = 'checking' | 'valid' | 'invalid';

function parseHashError(): { code: string | null; description: string | null } {
  // O Supabase devolve os parâmetros no fragmento (#...), não na query string.
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  const code = params.get('error_code') ?? params.get('error');
  const description = params.get('error_description');
  return {
    code,
    description: description ? decodeURIComponent(description.replace(/\+/g, ' ')) : null,
  };
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [tokenState, setTokenState] = useState<TokenState>('checking');
  const [tokenError, setTokenError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // 1) Erro explícito no fragmento da URL (token expirado/inválido/já usado).
    const hashError = parseHashError();
    if (hashError.code) {
      setTokenError(
        hashError.code.includes('expired')
          ? 'O link de recuperação expirou. Solicite um novo para redefinir sua senha.'
          : (hashError.description ??
            'O link de recuperação é inválido ou já foi utilizado. Solicite um novo.')
      );
      setTokenState('invalid');
      return;
    }

    // 2) O SDK emite PASSWORD_RECOVERY ao detectar o token de recuperação.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setTokenState('valid');
      }
    });

    // 3) Fallback: se já houver sessão (SDK processou o token antes do listener),
    //    consideramos o token válido. Sem sessão e sem erro no hash → inválido.
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setTokenState((prev) => (prev === 'checking' ? 'valid' : prev));
      } else {
        // Dá um curto intervalo para o evento PASSWORD_RECOVERY chegar.
        setTimeout(() => {
          setTokenState((prev) => {
            if (prev === 'checking') {
              setTokenError(
                'Link de recuperação inválido ou ausente. Solicite um novo para redefinir sua senha.'
              );
              return 'invalid';
            }
            return prev;
          });
        }, 1500);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      // Encerra a sessão temporária de recovery — o usuário entra com a nova senha.
      await supabase.auth.signOut();
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao redefinir a senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-raios-bg px-4">
      <div className="w-full max-w-sm md:max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/raios-mark.png"
            alt="RAIOS"
            className="inline-block w-14 h-14 rounded-2xl mb-4 object-cover"
          />
          <h1 className="text-3xl font-bold text-raios-text tracking-tight">RAIOS</h1>
          <p className="text-raios-muted text-sm mt-1">Definir nova senha</p>
        </div>

        {/* Card */}
        <Card className="p-0">
          <CardHeader>
            <CardTitle>Nova senha</CardTitle>
          </CardHeader>

          <CardContent>
            {tokenState === 'checking' && (
              <p className="text-sm text-raios-muted">Validando link de recuperação...</p>
            )}

            {tokenState === 'invalid' && (
              <div className="space-y-4">
                <p className="text-sm text-raios-danger">{tokenError}</p>
                <Link to="/forgot-password">
                  <Button type="button" variant="brand" className="w-full">
                    Solicitar novo link
                  </Button>
                </Link>
                <div className="text-center">
                  <Link to="/login" className="text-sm text-raios-accent hover:underline">
                    Voltar para o login
                  </Link>
                </div>
              </div>
            )}

            {tokenState === 'valid' && done && (
              <div className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Senha redefinida com sucesso! Redirecionando para o login...
                </p>
                <Link to="/login">
                  <Button type="button" variant="brand" className="w-full">
                    Ir para o login
                  </Button>
                </Link>
              </div>
            )}

            {tokenState === 'valid' && !done && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Escolha uma nova senha para sua conta.
                </p>
                <Input
                  label="Nova senha"
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Input
                  label="Confirmar nova senha"
                  type="password"
                  placeholder="Repita a senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  error={error || undefined}
                />

                <Button
                  type="submit"
                  variant="brand"
                  className="w-full"
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Redefinir senha'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
